import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import './HotelCategoryForm.css';

const HotelCategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    isActive: true,
    displayOrder: 0
  });

  useEffect(() => {
    if (isEdit) {
      fetchCategory();
    }
  }, [id]);

  const fetchCategory = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/hotel-categories/${id}`);
      const category = response.data.data;
      setFormData({
        name: category.name || '',
        slug: category.slug || '',
        isActive: category.isActive !== undefined ? category.isActive : true,
        displayOrder: category.displayOrder || 0
      });
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch hotel category');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };
    
    // Auto-generate slug from name if not editing or if name changed
    if (name === 'name' && !isEdit) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      newFormData.slug = slug;
    }
    
    setFormData(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }

    if (!formData.slug.trim()) {
      setError('Slug is required');
      return;
    }

    try {
      if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/hotel-categories/${id}`, formData);
      } else {
        await axios.post(`${API_BASE_URL}/admin/hotel-categories`, formData);
      }
      navigate('/sy-admin/hotel-categories');
    } catch (error) {
      setError(error.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} hotel category`);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="category-form-container">
          <div className="category-form-header">
            <div className="skeleton skeleton-text" style={{ width: '300px', height: '40px', borderRadius: '8px' }}></div>
            <div className="skeleton skeleton-button" style={{ width: '180px', height: '40px', borderRadius: '8px' }}></div>
          </div>
          <div className="category-form-card">
            <div className="form-section">
              <div className="skeleton skeleton-text" style={{ width: '200px', height: '32px', borderRadius: '8px', marginBottom: '24px' }}></div>
              <div className="form-row">
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '80px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
              </div>
              <div className="form-group">
                <div className="skeleton skeleton-text" style={{ width: '60px', height: '18px', borderRadius: '4px' }}></div>
              </div>
            </div>
            <div className="form-actions">
              <div className="skeleton skeleton-button" style={{ width: '150px', height: '44px', borderRadius: '8px' }}></div>
              <div className="skeleton skeleton-button" style={{ width: '100px', height: '44px', borderRadius: '8px' }}></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="category-form-container">
        <div className="category-form-header">
          <h1>{isEdit ? 'Edit Hotel Category' : 'Add New Hotel Category'}</h1>
          <button
            onClick={() => navigate('/sy-admin/hotel-categories')}
            className="btn btn-secondary"
          >
            ← Back to Categories
          </button>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="category-form-card">
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h2>Basic Information</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Category Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Hotel, Apartment, Home"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="slug">Slug *</label>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    required
                    placeholder="e.g., hotel, apartment, home"
                  />
                  <small>URL-friendly identifier {!isEdit && '(auto-generated from name)'}</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="displayOrder">Display Order</label>
                  <input
                    type="number"
                    id="displayOrder"
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleChange}
                    min="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                  <span>Active</span>
                </label>
                <small>Inactive categories won't be available for selection</small>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {isEdit ? 'Update Category' : 'Create Category'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/sy-admin/hotel-categories')}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default HotelCategoryForm;
